import React, { useMemo } from "react";
import { View, Text, StyleSheet, ActivityIndicator, Pressable } from "react-native";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  DataTable,
  Column,
} from "../../../components/adminDashboard/AICompanion";
import { colors, spacing } from "../../../styles/design-system";
import CustomText from "../../common/general/CustomText/CustomText";
import { TopQuestionItem, TopQuestionScopeFilter } from "@axio/graphql/generated/graphql";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { useTranslation } from "react-i18next";

type QuestionType = "assignment" | "course";

type Question = {
  id: string;
  question: string;
  context: string;
  frequency: number;
  type: QuestionType;
};

type Props = {
  questions: TopQuestionItem[];
  loading: boolean;
  allCount: number;
  assignmentCount: number;
  courseCount: number;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  scope: TopQuestionScopeFilter;
  onScopeChange: (scope: TopQuestionScopeFilter) => void;
};

const TabCount = ({ value }: { value: number }) => (
  <View style={styles.tabCountBadge}>
    <Text style={styles.tabCountText}>{value}</Text>
  </View>
);

export default function CommonQuestionsTab({
  questions,
  loading,
  allCount,
  assignmentCount,
  courseCount,
  page,
  pageSize,
  totalCount,
  onPageChange,
  scope,
  onScopeChange,
}: Props) {
  const { t } = useTranslation();
  const questionsLoading = loading;

  const scopeToString = (s: TopQuestionScopeFilter) => {
    if (s === TopQuestionScopeFilter.All) return "all";
    if (s === TopQuestionScopeFilter.Assignment) return "assignment";
    if (s === TopQuestionScopeFilter.Course) return "course";
    return "all";
  };

  const stringToScope = (val: string) => {
    if (val === "assignment") return TopQuestionScopeFilter.Assignment;
    if (val === "course") return TopQuestionScopeFilter.Course;
    return TopQuestionScopeFilter.All;
  };

  const activeTabValue = scopeToString(scope);

  const handleFilterChange = (value: string) => {
    onScopeChange(stringToScope(value));
  };

  const rows: Question[] = useMemo(() => {
    return questions.map((q) => ({
      id: `${q.courseId}-${q.assignmentId}-${q.questionText}`,
      question: q.questionText,
      context: q.scope === "assignment" ? `${q.courseName}: ${q.assignmentName}` : q.courseName || "Unknown course",
      frequency: q.frequency,
      type: q.scope === "assignment" ? "assignment" : "course",
    }));
  }, [questions]);

  const isQuestionEmpty = !questionsLoading && rows.length === 0;

  const columns: Column<Question>[] = [
    {
      key: "rank",
      flex: 0.08,
      renderCell: (_q, index) => (
        <View
          style={{
            alignItems: "flex-end",
          }}>
          <View
            style={{
              width: 24,
              height: 24,
              borderRadius: 12,
              backgroundColor: colors.grey[100],
              alignItems: "center",
              justifyContent: "center",
            }}>
            <Text
              style={{
                fontSize: 12,
                fontWeight: "600",
                color: colors.grey[600],
              }}>
              {(page - 1) * pageSize + index + 1}
            </Text>
          </View>
        </View>
      ),
    },
    {
      key: "question",
      flex: 5,
      renderCell: (q) => (
        <View>
          <Text style={{ fontWeight: "600" }}>{q.question}</Text>
          <CustomSpacing type="vertical" size="xs" />
          <Text style={{ color: "#667085" }}>{q.context}</Text>
        </View>
      ),
    },
    {
      key: "frequency",
      flex: 1,
      renderCell: (q) => (
        <View style={{ alignItems: "flex-end" }}>
          <Text style={{ fontWeight: "600" }}>{q.frequency}</Text>
        </View>
      ),
    },
  ];

  const totalPages = Math.ceil(totalCount / pageSize);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    const endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <View>
      <Tabs value={activeTabValue} onValueChange={handleFilterChange}>
        <View style={{ flexDirection: "row", alignItems: "center", marginBottom: spacing.sm }}>
          <TabsList style={styles.tabsList}>
            <TabsTrigger variant="default" value="all">
              <Text style={styles.tabTriggerText}>All</Text>
              <TabCount value={allCount} />
            </TabsTrigger>

            <TabsTrigger variant="default" value="assignment">
              <Text style={styles.tabTriggerText}>Assignment</Text>
              <TabCount value={assignmentCount} />
            </TabsTrigger>

            <TabsTrigger variant="default" value="course">
              <Text style={styles.tabTriggerText}>Course</Text>
              <TabCount value={courseCount} />
            </TabsTrigger>
          </TabsList>
        </View>

        <TabsContent value={activeTabValue}>
          {questionsLoading && (
            <View style={styles.loaderContainer}>
              <ActivityIndicator
                size="large"
                color="#0000ff"
                accessibilityLabel={t("commonQuestionsTab.loading", "Loading questions")}
                aria-label={t("commonQuestionsTab.loading", "Loading questions")}
              />
            </View>
          )}

          {isQuestionEmpty && (
            <View style={styles.loaderContainer}>
              <CustomText text="Not Available" size="s" style={styles.pendingText} />
            </View>
          )}

          {!questionsLoading && !isQuestionEmpty && (
            <DataTable columns={columns} data={rows} keyExtractor={(q) => q.id} />
          )}
        </TabsContent>
      </Tabs>

      {totalPages > 1 && (
        <View style={styles.paginationContainer}>
          <Pressable
            style={[styles.paginationButton, page === 1 && styles.paginationButtonDisabled]}
            disabled={page === 1}
            accessibilityLabel={t("commonQuestionsTab.previousPage", "Previous")}
            onPress={() => onPageChange(page - 1)}>
            <Text style={[styles.paginationButtonText, page === 1 && styles.paginationButtonTextDisabled]}>
              &larr; {t("commonQuestionsTab.previousPage", "Previous")}
            </Text>
          </Pressable>

          <View style={styles.pageNumbersContainer}>
            {pageNumbers.map((p) => (
              <Pressable
                key={p}
                style={[styles.pageNumberButton, page === p && styles.pageNumberButtonActive]}
                accessibilityLabel={t("commonQuestionsTab.goToPage", "Go to page {{page}}", { page: p })}
                accessibilityState={{ selected: page === p }}
                onPress={() => onPageChange(p)}>
                <Text style={[styles.pageNumberText, page === p && styles.pageNumberTextActive]}>{p}</Text>
              </Pressable>
            ))}
          </View>

          <Pressable
            style={[styles.paginationButton, page >= totalPages && styles.paginationButtonDisabled]}
            disabled={page >= totalPages}
            accessibilityLabel={t("commonQuestionsTab.nextPage", "Next")}
            onPress={() => onPageChange(page + 1)}>
            <Text style={[styles.paginationButtonText, page >= totalPages && styles.paginationButtonTextDisabled]}>
              {t("commonQuestionsTab.nextPage", "Next")} &rarr;
            </Text>
          </Pressable>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  tabsList: {
    marginBottom: spacing.lg,
  },
  tabTriggerText: {},
  tabCountBadge: {
    marginLeft: 6,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 12,
    backgroundColor: colors.grey[100],
    alignItems: "center",
    justifyContent: "center",
  },
  tabCountText: {
    fontSize: 12,
    fontWeight: "500",
    color: colors.grey[600],
  },
  loaderContainer: {
    flex: 3,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  pendingText: {
    color: "#6B7280",
    fontStyle: "italic",
  },
  paginationContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: spacing.xl,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
  },
  paginationButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: "transparent",
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationButtonText: {
    fontSize: 14,
    fontWeight: "500",
    color: colors.grey[700],
  },
  paginationButtonTextDisabled: {
    color: colors.grey[400],
  },
  pageNumbersContainer: {
    flexDirection: "row",
    gap: 4,
  },
  pageNumberButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: "center",
    alignItems: "center",
  },
  pageNumberButtonActive: {
    backgroundColor: colors.purple[600],
  },
  pageNumberText: {
    fontSize: 14,
    fontWeight: "500",
    color: colors.grey[700],
  },
  pageNumberTextActive: {
    color: colors.white,
  },
});
